import React from 'react';
import { css, cx } from '@emotion/css';
import { useNode, useEditor, ROOT_NODE } from '@craftjs/core';

import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import {
  VIDEO_NODE_NAME,
  VIDEO_NODE_HEIGHT,
  DEFAULT_INTERACTION_EVENT,
  NO_LINK_EVENT,
} from '@builder4/shared/utils/constants';
import type { TRBLNumberCOORDS } from '@builder4/shared/types/layout';
import type { NodeCustomProps } from '@builder4/shared/types/craft';
import type { VideoFitType } from '@builder4/shared/types/video';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getPositionStyle,
  getPaddingStyle,
  getMarginStyle,
  getBorderRadiusStyle,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
} from '@builder4/shared/utils/styleHelpers';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import type { StylesConfig } from '@builder4/shared/types/styles';
import {
  getHrefAttr,
  getTargetAttr,
  getRelHrefAttr,
} from '@builder4/shared/utils/attributesHelpers';
import {
  generateEmbedUrl,
  parseVideoUrl,
  extractIframeSrc,
} from '@builder4/shared/utils/videoHelpers';
import { VideoPropsDto } from '@builder4/shared/dto/craft/props/VideoPropsDto';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';

export type VideoProps = VideoPropsDto;

export const defaultProps: Partial<VideoProps> = {
  // Source
  sourceType: 'url',
  url: '',
  embedCode: '',
  videoProvider: 'youtube',
  thumbnailUrl: undefined,

  // Playback
  autoplay: false,
  loop: false,
  mute: false,
  controls: true,

  // Display
  fitType: 'fill',
  opacity: 1,

  // Dimensions (16:9 default)
  width: '100%',
  height: `${VIDEO_NODE_HEIGHT}px`,
  minWidth: 0,
  minHeight: 0,
  maxWidth: 1200,
  maxWidthUnit: 'px',
  aspectRatioLock: false,
  aspectRatio: undefined,

  // Layout
  padding: [0, 0, 0, 0],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  borderWidth: 0,
  borderColor: undefined,
  backgroundColor: undefined,

  // Position
  position: 'relative',
  top: '0px',
  left: '0px',
  right: undefined,
  bottom: undefined,
  zIndex: 0,
  positionAnchorsState: {
    enableTop: true,
    enableRight: false,
    enableBottom: false,
    enableLeft: true,
    enabled: false,
  },

  // Flex
  fillSpace: 'yes',
  flexBasis: '100%',
  flexGrow: 0,

  // Visibility
  visibility: DEFAULT_VISIBILITY,

  // Responsive
  tablet: {},
  mobile: {
    width: '100%',
    fillSpace: 'no',
    flexBasis: '100%',
  },
};

function computeVideoContentStyles(props: Partial<VideoProps>, activeGlobalStyles: StylesConfig) {
  const {
    padding,
    backgroundColor,
    borderColor,
    borderWidth,
    borderRadius,
    shadowType,
    shadowSettings,
  } = props;

  const borderRadiusString = borderRadius
    ? getBorderRadiusStyle(borderRadius as TRBLNumberCOORDS).borderRadius
    : undefined;
  const paddingString = padding ? getPaddingStyle(padding as TRBLNumberCOORDS).padding : undefined;
  const borderWidthString = borderWidth ? `${borderWidth}px` : undefined;
  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);
  const borderColorString = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);

  const hoverStylesObj = getHoverStyles(
    props.hoverEffect,
    props.hoverTransitionDuration,
    props.hoverIntensity,
    !!getBoxShadow(shadowType, shadowSettings),
  );

  return {
    padding: paddingString,
    background,
    border:
      borderWidth && borderColorString
        ? `${borderWidthString} solid ${borderColorString}`
        : undefined,
    borderRadius: borderRadiusString,
    ...hoverStylesObj,
    boxShadow: getBoxShadow(shadowType, shadowSettings),
  };
}

const videoContentStyle = (
  props: Partial<VideoProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
) => {
  const currentBpStyles = computeVideoContentStyles(
    {
      ...props,
      padding: props.padding || [0, 0, 0, 0],
      borderRadius: props.borderRadius || [0, 0, 0, 0],
    },
    activeGlobalStyles,
  );
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    pointerEvents: 'auto',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeVideoContentStyles(mobile, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeVideoContentStyles(tablet, activeGlobalStyles);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyle);
};

const computeVideoNodeStyles = (props: Partial<VideoProps>) => {
  const { height, width, maxWidth, maxWidthUnit, fillSpace, flexBasis, margin, zIndex } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const { aspectRatioLock, aspectRatio } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin).margin : undefined;

  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = isAspectRatioLocked ? `${aspectRatio} / 1` : undefined;

  // WHY: Unlike Image (which defaults to '%' for backward compat with old saved data),
  // Video's maxWidth inspector was always broken so no existing saved data has maxWidth set.
  // Defaulting to 'px' is correct — there is no legacy '%' data to protect against.
  const unit = maxWidthUnit || 'px';
  const maxWidthValue = maxWidth != null ? `${maxWidth}${unit}` : undefined;

  let flex: string | undefined;
  if (fillSpace) {
    if (fillSpace === 'yes') {
      if (flexBasis) {
        flex = `1 1 ${flexBasis}`;
      }
    } else {
      flex = 'unset';
    }
  }

  return {
    flex,
    padding: marginString,
    ...positionStyle,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    width: width || undefined,
    maxWidth: maxWidthValue,
    zIndex: zIndex || undefined,
    ...(aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
};

function getVideoNodeStylesObject(props: Partial<VideoProps>) {
  const currentBpStyles = computeVideoNodeStyles({
    ...props,
    margin: props.margin || [0, 0, 0, 0],
  });
  const finalStyles = {
    ...currentBpStyles,
    display: 'flex',
    mobile: {} as ReturnType<typeof computeVideoNodeStyles>,
    tablet: {} as ReturnType<typeof computeVideoNodeStyles>,
  };

  const { mobile, tablet } = props;
  if (mobile && Object.keys(mobile).length) {
    const positionProps = getPositionPropsV2(mobile as Partial<VideoProps>, props);
    const mobileStyles = computeVideoNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<VideoProps>);
    finalStyles.mobile = mobileStyles;
  }
  if (tablet && Object.keys(tablet).length) {
    const positionProps = getPositionPropsV2(tablet as Partial<VideoProps>, props);
    const tabletStyles = computeVideoNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<VideoProps>);
    finalStyles.tablet = tabletStyles;
  }

  return finalStyles;
}

const videoNodeStyle = (props: Partial<VideoProps>, isServerRender: boolean) => {
  const { mobile, tablet, ...baseStyles } = getVideoNodeStylesObject(props);
  const finalStyles: CSSInterpolation = { ...baseStyles };

  if (mobile && Object.keys(mobile).length) {
    finalStyles[getMediaQueryHeader(isServerRender, 'mobile')] = mobile;
  }
  if (tablet && Object.keys(tablet).length) {
    finalStyles[getMediaQueryHeader(isServerRender, 'tablet')] = tablet;
  }

  return css(finalStyles);
};

// fitType and opacity are not breakpoint-aware, so no media queries needed
const videoIframeStyleFromValues = (
  opacity: number | undefined,
  fitType: VideoFitType | undefined,
) => {
  return css({
    opacity: opacity || undefined,
    objectFit: fitType === 'fit' ? ('contain' as const) : ('cover' as const),
    width: '100%',
    height: '100%',
    border: 'none',
  });
};

// Style for the video thumbnail container in builder mode
const videoThumbnailContainerStyleFromValues = (opacity: number | undefined) => {
  return css({
    position: 'relative',
    width: '100%',
    height: '100%',
    opacity,
  });
};

// Style for the video thumbnail background
const videoThumbnailBackgroundStyleFromValues = (
  thumbnailUrl: string | null | undefined,
  fitType: VideoFitType | undefined,
) => {
  if (thumbnailUrl) {
    return css({
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${thumbnailUrl})`,
      backgroundSize: fitType === 'fit' ? 'contain' : 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#000',
    });
  }

  // For embed codes or when no thumbnail available
  return css({
    position: 'absolute',
    inset: 0,
    backgroundColor: '#000',
  });
};

const videoInteractionOverlayClass = css({
  position: 'absolute',
  inset: 0,
  zIndex: 10,
  cursor: 'pointer',
});

function getEmbedSrcFromValues(
  sourceType: 'url' | 'embed' | undefined,
  url: string | undefined,
  embedCode: string | undefined,
  autoplay: boolean | undefined,
  controls: boolean | undefined,
  loop: boolean | undefined,
  mute: boolean | undefined,
): string | null {
  const videoUrl =
    sourceType === 'url' ? url : sourceType === 'embed' ? extractIframeSrc(embedCode ?? '') : null;

  if (!videoUrl) {
    return null;
  }

  const parsed = parseVideoUrl(videoUrl);
  if (!parsed.isValid) {
    return null;
  }

  return generateEmbedUrl(parsed.provider, parsed.videoId, { autoplay, controls, loop, mute });
}

const Video = (props: Partial<VideoProps> & { children?: React.ReactNode }) => {
  const mergedProps = {
    ...defaultProps,
    ...props,
  };

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();

  const {
    id,
    connectors: { connect, drag },
  } = useNode((node) => ({
    id: node.id,
  }));

  const { breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Typed helper for breakpoint-aware prop access
  const getProp = <K extends keyof VideoProps>(key: K) =>
    getBreakpointProp(mergedProps, key, breakpoint);

  const visibility = getProp('visibility');
  const clickEvent = getProp('clickEvent');
  const sourceType = getProp('sourceType');
  const url = getProp('url');
  const embedCode = getProp('embedCode');
  const autoplay = getProp('autoplay');
  const controls = getProp('controls');
  const loop = getProp('loop');
  const mute = getProp('mute');
  const opacity = getProp('opacity');
  const fitType = getProp('fitType');
  const thumbnailUrl = getProp('thumbnailUrl');

  const { children } = mergedProps;

  const visibilityClasses = getVisibilityClasses(visibility || DEFAULT_VISIBILITY);
  const effectiveClickEvent = clickEvent || DEFAULT_INTERACTION_EVENT;
  const shouldWrapWithLink = effectiveClickEvent.eventType !== NO_LINK_EVENT;
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';
  const isBuilderMode = environment === 'builder';

  const embedSrc = getEmbedSrcFromValues(
    sourceType,
    url,
    embedCode,
    autoplay,
    controls,
    loop,
    mute,
  );
  const hasVideo = Boolean(embedSrc);

  const animationClass = getAnimationInitialClass(mergedProps.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(mergedProps);

  const videoElement = React.createElement(
    shouldWrapWithLink ? 'a' : 'div',
    {
      ref: (ref: HTMLElement | null) => {
        if (ref) connect(drag(ref));
      },
      className: cx(
        'video-content',
        isEditable && 'node-dashed-indicator boundary-indicator styled-node',
        videoContentStyle(mergedProps, isServerRender, activeGlobalStyles),
        animationClass,
      ),
      ...animationAttrs,
      ...(shouldWrapWithLink
        ? {
            href: getHrefAttr(effectiveClickEvent),
            target: getTargetAttr(effectiveClickEvent),
            rel: getRelHrefAttr(effectiveClickEvent),
            onClick: (e: React.MouseEvent) => e.preventDefault(),
          }
        : {}),
      ...(environment === 'builder' ? { 'data-animation-preview': id } : {}),
    },
    <>
      {!hasVideo && children}
      {hasVideo && embedSrc && (
        <>
          {isBuilderMode ? (
            // In Builder mode, show a static thumbnail
            <div className={videoThumbnailContainerStyleFromValues(opacity)}>
              {/* Thumbnail background */}
              <div className={videoThumbnailBackgroundStyleFromValues(thumbnailUrl, fitType)} />
              {/* Invisible overlay to prevent interaction */}
              <div className={videoInteractionOverlayClass} />
            </div>
          ) : (
            // In Preview/Server mode, show the actual iframe
            <iframe
              src={embedSrc}
              className={videoIframeStyleFromValues(opacity, fitType)}
              allow={`accelerometer;${autoplay ? ' autoplay;' : ''} clipboard-write; encrypted-media; gyroscope; picture-in-picture`}
              allowFullScreen
              title="Video player"
            />
          )}
        </>
      )}
    </>,
  );

  // Wrap with video-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    // Return null if no video to match frontend behavior
    if (!hasVideo) return null;

    return (
      <div
        className={cx(
          'video-node',
          visibilityClasses,
          animationClass,
          videoNodeStyle(mergedProps, isServerRender),
        )}
        {...animationAttrs}
      >
        {videoElement}
      </div>
    );
  }

  return videoElement;
};

Video.craft = {
  displayName: VIDEO_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: VIDEO_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export { videoNodeStyle, getVideoNodeStylesObject };
export default Video;
